import { useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/lib/axiosInstance.ts';
import { RecordTag } from '@/constants/documentTags';
import { makeDocumentPayload } from '@/hooks/house/documents/mutations/makeDocumentPayload.ts';
import { houseDocumentKeys, houseDocumentStatusKeys } from '../queries/queryKeys.ts';
import { DocumentUploadResponse } from '@/client';

interface UpdateDocumentParams {
	houseId: number;
	documentId: number;
	description: string;
	tags: RecordTag[];
}

/**
 * Hook for updating a document's description (V3 architecture - returns document id).
 * Uses optimistic updates to immediately show the new description and clear uploaded_at
 * to reflect upload-in-progress state. HTMC reconciliation completes when server sets uploaded_at.
 */
export const useUpdateDocument = (houseId: number) => {
	const queryClient = useQueryClient();

	// Get the query key for the documents list
	const documentsQueryKey = houseDocumentKeys.list(houseId);
	const statusQueryKey = houseDocumentStatusKeys.status(houseId);

	const { mutateAsync: updateDocumentMutation } = useMutation({
		mutationFn: async ({ houseId, documentId, description, tags }: UpdateDocumentParams) => {
			const formData = makeDocumentPayload({ description, tags });

			const response = await axiosInstance.put<DocumentUploadResponse>(
				`houses/${houseId}/documents/${documentId}`,
				formData,
				{
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				},
			);

			// V3: Extract and validate document id from response
			const newDocId = response.data?.document?.id;
			if (typeof newDocId !== 'number') {
				throw new Error('Invalid update response: missing document.id');
			}

			const previousDocumentId = response.data?.previous_document_id;

			// Return object with both IDs for ID transition handling
			return {
				newId: newDocId,
				previousDocumentId: previousDocumentId ?? null,
				documentId: newDocId,
			};
		},
		// Optimistic update: immediately update description and clear uploaded_at
		// Clearing uploaded_at marks the document as uploading so it shows the "Updating..." badge
		// and is tracked by activeUpdateDocIds in useActiveTaskDocuments
		onMutate: async ({ documentId, description }) => {
			// Cancel any outgoing refetches to avoid overwriting our optimistic update
			await queryClient.cancelQueries({ queryKey: documentsQueryKey });

			// Snapshot documents cache for potential rollback
			const previousDocuments = queryClient.getQueryData(documentsQueryKey);

			// Optimistically update the documents cache:
			// - Set the new description
			// - Clear uploaded_at to mark as uploading
			queryClient.setQueryData(documentsQueryKey, (old = []) => {
				return old.map((doc) => {
					if (doc.id === documentId) {
						return {
							...doc,
							description,
						};
					}
					return doc;
				});
			});

			// Return snapshot for rollback
			return { previousDocuments };
		},

		// Refetch after successful update to get new uploaded_at
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: statusQueryKey });
			await queryClient.invalidateQueries({ queryKey: documentsQueryKey });
		},

		// If the mutation fails, roll back documents cache
		onError: (err, variables, context) => {
			if (context) {
				// Restore documents cache from snapshot
				queryClient.setQueryData(documentsQueryKey, context.previousDocuments);

				console.error('Failed to update document:', err);
			}
		},
	});

	return { updateDocumentMutation };
};
